import React, { useEffect, useState } from 'react';

import { bemCssModules, correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';

import { getTestId } from '@utils';

import {
  CryptoCurrencyEnum,
  CurrencyEnum,
  FiatCurrencyEnum,
  CurrencyDisplayOptionsType,
} from '@modules/global/types';

import MoneyInputOld from '@components/MoneyInputOld';
import Preloader from '@components/Preloader';
import Select from '@components/Select';

import Limits, { TLimits } from './Limits';
import ArrowRightIcon from './arrow_right.svg';
import useConvert, { GetRate } from './useConvert';

import style from './ConverterOld.scss';

const cn = bemCssModules(style, 'ConverterOld');
const getOptions = (array: CurrencyEnum[]) => array.map(item => ({ value: item, label: item }));
const ti = getTestId('ConverterOld');

export type { RateType, ErrorType } from './useConvert';

export type ConverterOldDataType = {
  [k in 'from' | 'to']: {
    amount: string;
    currency: CurrencyEnum;
  };
} & { inputCurrency: CurrencyEnum };

export type TConverterOld = {
  config: {
    [key in 'from' | 'to']: {
      currencies: CurrencyEnum[];
      defaultAmount?: string;
      defaultCurrency?: CurrencyEnum;
    };
  } & { displayOptions?: CurrencyDisplayOptionsType };
  onChange?: (data: ConverterOldDataType) => void;
  getRate?: GetRate;
  isLoading?: boolean;
  disabled?: boolean;
  limits?: TLimits['limits'];
  paramsRequireRateUpdate?: React.DependencyList;
};

export const ConverterOld: CustomFC<TConverterOld> = props => {
  const {
    className,
    config,
    onChange,
    getRate,
    isLoading,
    disabled,
    limits,
    paramsRequireRateUpdate = [],
  } = props;

  const fromCurrencies = getOptions(config.from.currencies);
  const toCurrencies = getOptions(config.to.currencies);
  const [fromAmount, setFromAmount] = useState(config.from.defaultAmount || '');
  const defaultFromCurrency = config.from.defaultCurrency || FiatCurrencyEnum.USD;
  const [fromCurrency, setFromCurrency] = useState(defaultFromCurrency);

  const [toAmount, setToAmount] = useState(config.to.defaultAmount || '');
  const defaultToCurrency = config.to.defaultCurrency || CryptoCurrencyEnum.BTC;
  const [toCurrency, setToCurrency] = useState(defaultToCurrency);

  const defaultInputCurrency = fromAmount ? fromCurrency : toAmount ? toCurrency : fromCurrency;
  const [inputCurrency, setInputCurrency] = useState(defaultInputCurrency);
  const [conversionIsNeeded, setConversionIsNeeded] = useState(false);

  const [convert, rate, error] = useConvert(getRate, config.displayOptions);

  const convertAction = () => {
    if (+fromAmount || +toAmount) {
      if (inputCurrency === fromCurrency) {
        convert(fromCurrency, toCurrency, fromAmount);
      } else {
        convert(toCurrency, fromCurrency, toAmount);
      }
    }
  };

  useEffect(() => {
    if (paramsRequireRateUpdate) convertAction();
  }, paramsRequireRateUpdate);

  useEffect(() => {
    if (conversionIsNeeded) {
      convertAction();
      setConversionIsNeeded(false);
    }
  }, [conversionIsNeeded]);

  const fromError = inputCurrency === fromCurrency && error;
  const toError = inputCurrency === toCurrency && error;

  useEffect(() => {
    const correctingFromAmount = correctingTrailingNumbers(
      rate?.total?.[fromCurrency] || fromAmount,
      config.displayOptions?.[fromCurrency]?.display_digits,
      config.displayOptions?.[fromCurrency]?.total_digits
    );
    const correctingToAmount = correctingTrailingNumbers(
      rate?.subtotal?.[toCurrency] || toAmount,
      config.displayOptions?.[toCurrency]?.display_digits,
      config.displayOptions?.[toCurrency]?.total_digits
    );

    setFromAmount(correctingFromAmount);
    setToAmount(correctingToAmount);
  }, [rate]);

  useEffect(() => {
    setToCurrency(defaultToCurrency);
  }, [defaultToCurrency]);

  useEffect(() => {
    setFromCurrency(defaultFromCurrency);
  }, [defaultFromCurrency]);

  useEffect(() => {
    if (onChange) {
      onChange({
        from: { amount: fromAmount, currency: fromCurrency },
        to: { amount: toAmount, currency: toCurrency },
        inputCurrency,
      });
    }
  }, [fromAmount, fromCurrency, toAmount, toCurrency, inputCurrency]);

  const onChangeFromAmount = (value: string) => {
    if (value === fromAmount) return;
    if (inputCurrency !== fromCurrency) setInputCurrency(fromCurrency);
    setFromAmount(value);
    if (!value || +value <= 0) {
      setToAmount('');
    }
    setConversionIsNeeded(true);
  };

  const onChangeFromCurrency = e => {
    const { value } = e.target;
    if (inputCurrency === fromCurrency) {
      setInputCurrency(value);
    }
    setFromCurrency(value);
    setConversionIsNeeded(true);
  };

  const onChangeToAmount = (value: string) => {
    if (value === toAmount) return;
    if (inputCurrency !== toCurrency) setInputCurrency(toCurrency);
    setToAmount(value);
    if (!value || +value <= 0) {
      setFromAmount('');
    }
    setConversionIsNeeded(true);
  };

  const onChangeToCurrency = e => {
    const { value } = e.target;
    setToCurrency(value);
    if (inputCurrency === toCurrency) {
      setInputCurrency(value);
    }
    setToAmount('');
    setConversionIsNeeded(true);
  };

  const getTotalDigits = (currency: CurrencyEnum) =>
    config.displayOptions?.[currency]?.total_digits;

  const isDisabled = (currencies: { value: CurrencyEnum; label: CurrencyEnum }[]) =>
    disabled || currencies.length < 2;

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <div className={cn('group')}>
        <MoneyInputOld
          className={cn('input')}
          value={fromAmount}
          onChange={onChangeFromAmount}
          totalDigits={getTotalDigits(fromCurrency)}
          disabled={disabled}
          testId={ti('input')}
        />
        <Select
          className={cn('select')}
          options={fromCurrencies}
          value={fromCurrency}
          onChange={onChangeFromCurrency}
          disabled={isDisabled(fromCurrencies)}
        />
        {fromError ? (
          <div className={cn('error')}>{fromError}</div>
        ) : (
          <Limits
            className={cn('limits')}
            limits={limits}
            currency={fromCurrency}
            displayOptions={config.displayOptions}
            onClick={onChangeFromAmount}
            testId={ti('limits')}
          />
        )}
      </div>

      <ArrowRightIcon className={cn('icon')} />

      <div className={cn('group')} data-testid={ti('group')}>
        <MoneyInputOld
          className={cn('input')}
          value={toAmount}
          onChange={onChangeToAmount}
          totalDigits={getTotalDigits(toCurrency)}
          disabled={disabled}
          testId={ti('input')}
        />
        <Select
          className={cn('select')}
          options={toCurrencies}
          value={toCurrency}
          onChange={onChangeToCurrency}
          disabled={isDisabled(toCurrencies)}
        />
        {toError ? (
          <div className={cn('error')}>{toError}</div>
        ) : (
          <Limits
            className={cn('limits')}
            limits={limits}
            currency={toCurrency}
            displayOptions={config.displayOptions}
            onClick={onChangeToAmount}
            testId={ti('limits')}
          />
        )}
      </div>
      {isLoading && <Preloader className={cn('loader')} />}
    </div>
  );
};

export default ConverterOld;
